/**
 * @name: Nulo A11y Utilities
 * @description: Focus management, live regions, and element helpers for Nulo sections
 */

export { prefersReducedMotion } from '@theme/utilities';

const TABBABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
  'summary',
  'iframe',
  'object',
  'embed',
  'video[controls]',
  'audio[controls]',
].join(',');

const FOCUSABLE_SELECTOR = [
  'button',
  '[href]',
  'input',
  'select',
  'textarea',
  '[tabindex="0"]',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
].join(',');

const INTERACTIVE_SELECTOR = `${TABBABLE_SELECTOR},${FOCUSABLE_SELECTOR}`;

/** @type {HTMLElement | null} */
let savedFocusElement = null;

/** @type {HTMLElement | null} */
let liveRegionEl = null;

/**
 * @param {unknown} target
 * @returns {HTMLElement | null}
 */
export function resolveElement(target) {
  if (!target) return null;
  if (target instanceof HTMLElement) return target;
  if (typeof target === 'string') return document.querySelector(target);
  return null;
}

/**
 * Resolves when `element` is near the viewport (or immediately if IO is unavailable).
 *
 * @param {Element | null | undefined} element
 * @param {{ rootMargin?: string, threshold?: number | number[] }} [options]
 * @returns {Promise<void>}
 */
export function whenNearViewport(element, options = {}) {
  return new Promise((resolve) => {
    if (!(element instanceof Element)) {
      resolve();
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      resolve();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          resolve();
          break;
        }
      },
      {
        rootMargin: options.rootMargin ?? '200px 0px',
        threshold: options.threshold ?? 0,
      }
    );

    observer.observe(element);
  });
}

/**
 * @param {HTMLElement | null | undefined} el
 */
export function safeFocus(el) {
  if (!el || typeof el.focus !== 'function') return;

  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isElementVisible(el) {
  if (!el || el.getAttribute('aria-hidden') === 'true') return false;
  if (el.hasAttribute('hidden') || el.disabled) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (parseFloat(style.opacity) === 0 && style.pointerEvents === 'none') return false;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  if (!el.getClientRects().length && style.position !== 'fixed') return false;
  return true;
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isElementTabbable(el) {
  if (!isElementVisible(el)) return false;

  const tag = el.tagName;
  const tabIndexAttr = el.getAttribute('tabindex');
  const hasTabIndexAttr = tabIndexAttr !== null && tabIndexAttr !== '';

  if (hasTabIndexAttr) {
    const parsed = parseInt(tabIndexAttr, 10);
    return !Number.isNaN(parsed) && parsed >= 0;
  }

  if (tag === 'A' || tag === 'AREA') return el.hasAttribute('href');
  if (tag === 'BUTTON' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') return el.type !== 'hidden';
  if (tag === 'SUMMARY') return true;
  if (el.isContentEditable) return true;
  if (tag === 'IFRAME' || tag === 'OBJECT' || tag === 'EMBED') return true;
  if (tag === 'VIDEO' || tag === 'AUDIO') return el.hasAttribute('controls');

  const role = el.getAttribute('role');
  if (
    role === 'button' ||
    role === 'link' ||
    role === 'checkbox' ||
    role === 'radio' ||
    role === 'tab' ||
    role === 'menuitem' ||
    role === 'option'
  ) {
    return el.tabIndex >= 0;
  }

  return el.hasAttribute('href');
}

/**
 * @param {string | string[] | undefined} additional
 * @returns {string}
 */
function buildTabbableSelector(additional) {
  if (!additional) return INTERACTIVE_SELECTOR;
  const extra = Array.isArray(additional) ? additional.join(',') : String(additional);
  return extra ? `${INTERACTIVE_SELECTOR},${extra}` : INTERACTIVE_SELECTOR;
}

/**
 * @param {HTMLElement[]} list
 * @param {HTMLElement} el
 */
function pushUnique(list, el) {
  if (!list.includes(el)) list.push(el);
}

/**
 * @param {HTMLElement | string | null | undefined} container
 * @param {string | string[] | undefined} [additionalSelectors]
 * @returns {HTMLElement[]}
 */
export function getTabbableElements(container, additionalSelectors) {
  const root = resolveElement(container);
  if (!root) return [];

  const selector = buildTabbableSelector(additionalSelectors);
  const nodes = root.querySelectorAll(selector);
  /** @type {HTMLElement[]} */
  const tabbables = [];

  for (const node of nodes) {
    if (node instanceof HTMLElement && isElementTabbable(node)) {
      pushUnique(tabbables, node);
    }
  }

  return tabbables;
}

/**
 * @param {HTMLElement | string | null | undefined} container
 * @param {string | string[] | undefined} [additionalSelectors]
 * @returns {HTMLElement | null}
 */
export function focusFirstTabbable(container, additionalSelectors) {
  const tabbables = getTabbableElements(container, additionalSelectors);
  if (!tabbables.length) return null;
  safeFocus(tabbables[0]);
  return tabbables[0];
}

/**
 * @returns {HTMLElement | null}
 */
export function saveFocus() {
  const active = document.activeElement;
  savedFocusElement =
    active instanceof HTMLElement && active !== document.body ? active : null;
  return savedFocusElement;
}

/**
 * @returns {boolean}
 */
export function restoreFocus() {
  if (!(savedFocusElement instanceof HTMLElement)) {
    savedFocusElement = null;
    return false;
  }

  safeFocus(savedFocusElement);
  savedFocusElement = null;
  return true;
}

/**
 * @typedef {Object} FocusTrapOptions
 * @property {string | string[]} [additionalSelectors]
 * @property {HTMLElement} [initialFocus]
 * @property {boolean} [focusOnActivate=true]
 */

/**
 * Trap focus within a container. Returns a deactivate function — call it on close.
 *
 * @param {HTMLElement | string | null | undefined} container
 * @param {(root: HTMLElement, event: KeyboardEvent) => void} [onEscape]
 * @param {FocusTrapOptions | string | string[] | undefined} [options]
 * @returns {() => void}
 */
export function createFocusTrap(container, onEscape, options) {
  const root = resolveElement(container);
  if (!root) return () => {};

  const resolvedOptions =
    options && typeof options === 'object' && !Array.isArray(options) && !(options instanceof HTMLElement)
      ? options
      : { additionalSelectors: options };

  const { additionalSelectors, initialFocus, focusOnActivate = true } = resolvedOptions;

  const refreshTabbables = () => getTabbableElements(root, additionalSelectors);

  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onEscape?.(root, event);
      return;
    }

    if (event.key !== 'Tab') return;

    const tabbables = refreshTabbables();
    if (!tabbables.length) {
      event.preventDefault();
      return;
    }

    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    const active = document.activeElement;

    if (tabbables.length === 1) {
      event.preventDefault();
      safeFocus(first);
      return;
    }

    if (event.shiftKey) {
      if (active === first || !root.contains(active)) {
        event.preventDefault();
        safeFocus(last);
      }
      return;
    }

    if (active === last || !root.contains(active)) {
      event.preventDefault();
      safeFocus(first);
    }
  };

  /** @param {FocusEvent} event */
  const handleFocusIn = (event) => {
    if (!(event.target instanceof Node) || root.contains(event.target)) return;

    const trapped = refreshTabbables();
    if (trapped.length) safeFocus(trapped[0]);
  };

  if (focusOnActivate) {
    if (initialFocus instanceof HTMLElement && root.contains(initialFocus)) {
      safeFocus(initialFocus);
    } else {
      focusFirstTabbable(root, additionalSelectors);
    }
  }

  root.addEventListener('keydown', handleKeyDown);
  document.addEventListener('focusin', handleFocusIn);

  return () => {
    root.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('focusin', handleFocusIn);
  };
}

/**
 * @param {string} message
 * @param {'polite' | 'assertive'} [priority='polite']
 */
export function announce(message, priority = 'polite') {
  if (!message) return;

  if (!liveRegionEl?.parentNode) {
    liveRegionEl = document.createElement('div');
    liveRegionEl.setAttribute('role', 'status');
    liveRegionEl.setAttribute('aria-live', priority === 'assertive' ? 'assertive' : 'polite');
    liveRegionEl.setAttribute('aria-atomic', 'true');
    liveRegionEl.className = 'visually-hidden';
    document.body.appendChild(liveRegionEl);
  } else {
    liveRegionEl.setAttribute('aria-live', priority === 'assertive' ? 'assertive' : 'polite');
  }

  liveRegionEl.textContent = '';
  window.setTimeout(() => {
    if (liveRegionEl) liveRegionEl.textContent = message;
  }, 30);
}
